/* eslint-disable no-nested-ternary */
import * as React from 'react';
import styled from '@emotion/styled';
import { Spacings } from '@styles/index';
import H5 from '@base/H5/H5';
import P2 from '@base/P2/P2';
import Divider from '@base/Divider/Divider';
import { IGroupUser, ILatestActivity } from 'types/groups';
import isEqual from 'lodash/isEqual';
import PopupPointer from '@icon/Groups/PopupPointer';
import GroupsUserProgress from '../GroupsUserProgress';
import LatestActivity from './LatestActivity';
import DailyStreaks from './DailyStreaks';

interface ContainerProps {
  placement: 'top' | 'bottom';
}

const Container = styled.div<ContainerProps>`
  display: grid;
  justify-items: center;
  min-width: 291px;
  padding: ${Spacings.SPACING_4B};
  border: ${Spacings.SPACING_1} solid var(--color-divider);
  background: var(--color-base-1);
  border-radius: ${Spacings.SPACING_2B};
  position: relative;
  margin-top: ${({ placement }) =>
    placement === 'bottom' ? Spacings.SPACING_4B : Spacings.SPACING_0B};
  margin-bottom: ${({ placement }) =>
    placement === 'top' ? Spacings.SPACING_4B : Spacings.SPACING_0B};
`;

const InfoWrapper = styled.div`
  margin-top: ${Spacings.SPACING_7B};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const ProgressInfoWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
`;

const StyP2 = styled(P2)`
  color: var(--color-text-secondary);
`;

const StyH5 = styled(H5)`
  color: ${(props) => props.color};
`;

const ProgressInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const StyDivider = styled(Divider)`
  margin-right: 10px;
  margin-left: 10px;
  height: ${Spacings.SPACING_6B};
  width: ${Spacings.SPACING_1};
`;

const StySeparator = styled(Divider)`
  margin-top: ${Spacings.SPACING_3B};
  margin-bottom: ${Spacings.SPACING_3B};
  height: ${Spacings.SPACING_1};
  width: 100%;
`;

const Arrow = styled(PopupPointer)`
  height: 11px;
  width: 27px;
  position: absolute;
  z-index: 1;
  transform: ${({ placement }) =>
    placement === 'top'
      ? 'translateY(100%) rotate(180deg)'
      : 'translateY(-100%) rotate(0deg)'};
  bottom: ${({ placement }) => (placement === 'top' ? 0 : 'unset')};
`;

const ArrowDown = styled(Arrow)``;

export interface IProfilePopupContentProps {
  selectedUser: IGroupUser;
  groupUID: string;
  closeDrawerAndPopup: () => void;
}

const ProfilePopupContent: React.FC<IProfilePopupContentProps> = ({
  selectedUser,
  groupUID,
  closeDrawerAndPopup
}) => {
  const { latestActivity, questionsAttempted, totalWins, watchMins, uid } =
    selectedUser;

  const hasLatestActivity = latestActivity && !isEqual(latestActivity, {});
  const [placement, setPlacement] =
    React.useState<ContainerProps['placement']>('bottom');

  React.useEffect(() => {
    const drawer = document.getElementById('GroupsLearnerPopup');
    if (drawer) {
      const updatedPlacement = drawer.getAttribute(
        'x-placement'
      ) as ContainerProps['placement'];
      setPlacement(updatedPlacement);
    }
  }, [selectedUser]);

  return (
    <Container onClick={(e) => e.stopPropagation()} placement={placement}>
      <Arrow
        viewBox="0 0 27 11"
        width="27px"
        height="11px"
        color="var(--color-divider)"
        fill="var(--color-base-1)"
        placement={placement}
      />
      <GroupsUserProgress
        user={selectedUser}
        avatarSize="54px"
        outerProgressSize="86px"
        innerProgressSize="70px"
        outerDashOffset={200}
        innerDashOffset={160}
      />
      <InfoWrapper>
        {hasLatestActivity && (
          <LatestActivity
            latestActivity={latestActivity as ILatestActivity}
            closeDrawerAndPopup={closeDrawerAndPopup}
          />
        )}
        <ProgressInfoWrapper>
          <ProgressInfo>
            <StyH5 data-testid="groups-user-mins" color="var(--color-i-blue)">
              {watchMins}
            </StyH5>
            <StyP2>Mins learnt</StyP2>
          </ProgressInfo>
          <StyDivider />
          <ProgressInfo>
            <StyH5
              data-testid="groups-user-questions"
              color="var(--color-i-green)"
            >
              {questionsAttempted}
            </StyH5>
            <StyP2>Q&apos;s attempted</StyP2>
          </ProgressInfo>
          <StyDivider />
          <ProgressInfo>
            <StyH5
              data-testid="groups-user-wins"
              color="var(--color-purple-dark)"
            >
              {totalWins}
            </StyH5>
            <StyP2>Week wins</StyP2>
          </ProgressInfo>
        </ProgressInfoWrapper>
        <StySeparator />
        <DailyStreaks groupUID={groupUID} userUID={uid} />
      </InfoWrapper>
    </Container>
  );
};

export default ProfilePopupContent;
